import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import LoadingSkeletonProductList from '@/components/product/LoadingSkeletonProductList';
import ProductList from '@/components/product/ProductList';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionProduct } from '@/interfaces/api/product/ICollectionProduct';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const StorePage = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const { storeName } = useParams<{ storeName: string }>();

	const {
		data: products,
		isLoading: productsIsLoading,
		isError: productIsError,
		error: productError,
	} = useQuery<ICollectionProduct>({
		queryKey: [
			ApiQueryKeys.getAllHaggleProducts,
			pageSize,
			pageNumber,
			storeName,
		],
		queryFn: () =>
			haggleApiService.getAllHaggleProducts({
				params: {
					'page[size]': pageSize,
					'page[number]': pageNumber,
					'filterQuery[tradeName]': storeName,
				},
			}),
		refetchOnWindowFocus: false,
	});

	if (productIsError) {
		if (productError instanceof Error) {
			notificationService.error(productError.message);
		} else {
			notificationService.error('Unknown error fetching products');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionProduct,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	return (
		<div>
			<div className="text-center pb-6 pt-10 max-w-[700px] mx-auto border-b">
				<h1
					className="text-redBrand-500 text-4xl font-bold"
					data-test="store-title"
				>
					{`Welcome to ${storeName}`}
				</h1>
				<h2 className="text-redBrand-500 text-2xl" data-test="store-subtitle">
					{`Where you will find ${
						products ? products?.itemCount : 0
					} products on Haggle`}
				</h2>
			</div>
			<div></div>
			{productsIsLoading && <LoadingSkeletonProductList />}
			{products && !productsIsLoading && (
				<ProductList
					collectionProduct={products}
					paginationOption={handlePaginationOptions(products)}
				/>
			)}
			<p className="text-center mb-4 ">
				<Link
					to="/"
					className="hover:text-redBrand-600 font-medium"
					data-test="link-to-home"
				>
					See more stores
				</Link>
			</p>
		</div>
	);
};

export default StorePage;
